import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigationType } from 'react-router';

import { useDispatch } from '@tmapy/redux';
import { useMessage } from '@tmapy/intl';

import { WaitingAlert, DangerAlert, Table, InfoAlert, BtnBar } from '@tmapy/style-guide';

import { SvuListItem } from './SvuListItem';
import { SvuFilter } from './SvuFilter';
import { svuMessages as msg, svuBtnMessages, svuQueryMessages } from './svuMessages';

import { SSE_TYPE, useSelector, useSSE } from '../../hooks';
import { QUERY_STATE } from '../../constants';
import { TableWithAsideLayout } from '../../app/components/TableWithAsideLayout';
import { FilterBtn } from '../../app/components/FilterBtn';
import { MainContent } from '../../app/components/MainContent';
import { PageTitle } from '../../app/components/PageTitle';
import { LoadNextItemsBar } from '../../app/components/LoadNextItemsBar';

import { useR7UrceniEditoraZpsListResource } from '../../openapi/operations/r7UrceniEditoraZpsList';

export const SvuList: React.FC = () => {
  const { incomingSSEEventType } = useSSE(SSE_TYPE.SVU);

  const dispatch = useDispatch();
  const navigationType = useNavigationType();
  const formatMessage = useMessage();
  const [isFilterOpen, setFilterOpen] = useState(false);
  const filters = useSelector((state) => state.filter.svuList);

  const query: Record<string, string | number> = {};
  for (const [key, val] of Object.entries(filters)) {
    if (typeof val !== 'undefined' && val !== '') {
      (query as any)[key] = val;
    }
  }

  const { queryState, data, fetchMore, refetch } = useR7UrceniEditoraZpsListResource(
    query,
    navigationType !== 'PUSH',
  );
  useEffect(() => {
    if (incomingSSEEventType === SSE_TYPE.SVU) {
      refetch();
    }
  }, [incomingSSEEventType]);

  const svuList = data?.results ?? [];
  const svuCount = data?.count;

  function handleNextSvuBtnClick() {
    if (data?.next) {
      fetchMore(data.next);
    }
  }

  function handleFilterBtnClick() {
    setFilterOpen(!isFilterOpen);
  }

  const isFiltered = Object.values(filters).some((filter) => filter);

  return (
    <TableWithAsideLayout
      asideLabel={formatMessage(svuBtnMessages.filter)}
      isAsideOpen={isFilterOpen}
      onAsideClose={handleFilterBtnClick}
    >
      <MainContent width='fit' scrollRestorationPrefix='svulist'>
        <article className='sg-u-vs-2'>
          <header className='sg-a-d-f sg-a-jc-sb sg-a-ai-b sg-a-cg-2'>
            <PageTitle>
              <FormattedMessage {...msg.pageTitle} />
            </PageTitle>
            <span className='sg-a-f-a' />

            <BtnBar>
              <FilterBtn isFiltered={isFiltered} onClick={handleFilterBtnClick} />
            </BtnBar>
          </header>
          {queryState === QUERY_STATE.WAITING && (
            <WaitingAlert>
              <FormattedMessage {...svuQueryMessages.waiting} />
            </WaitingAlert>
          )}
          {queryState === QUERY_STATE.FAIL && (
            <DangerAlert>
              <FormattedMessage {...svuQueryMessages.fail} />
            </DangerAlert>
          )}
          {svuList.length > 0 ? (
            <div className='sg-u-box sg-a-px-2 sg-a-pb-2 sg-u-vs-2'>
              <div className='sg-a-mx--2'>
                <Table isClickable>
                  <thead>
                    <tr>
                      <th className='sg-a-ta-c'>
                        <FormattedMessage {...msg.state} />
                        <br />
                        <FormattedMessage {...msg.idUzemniEditace} />
                      </th>
                      <th>
                        <FormattedMessage {...msg.name} />
                      </th>
                      <th>
                        <FormattedMessage {...msg.vyjadreniPrijato} />
                      </th>
                      <th>
                        <FormattedMessage {...msg.vyjadreniEditor} />
                      </th>
                      <th className='sg-a-ta-r'>
                        <span className='dmx-u-c-cta-green'>
                          <FormattedMessage {...msg.created} />
                        </span>
                        <>
                          <br />
                          <FormattedMessage {...msg.edited} />
                        </>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {svuList.map((svuItem) => (
                      <SvuListItem key={svuItem.id} {...svuItem} />
                    ))}
                  </tbody>
                </Table>
              </div>
              <LoadNextItemsBar
                count={svuList.length}
                total={svuCount!}
                queryState={queryState}
                onNextBtnClick={handleNextSvuBtnClick}
              />
            </div>
          ) : (
            queryState === QUERY_STATE.SUCCESS && (
              <InfoAlert>
                <FormattedMessage {...msg.filteredEmpty} />
              </InfoAlert>
            )
          )}
        </article>
      </MainContent>
      <SvuFilter filters={filters} dispatch={dispatch} />
    </TableWithAsideLayout>
  );
};
